import { useResetFinalPriceOnChange } from "@/services/backend/rlt/pricing/lib/use-reset-final-price-on-change";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  useRltOfferShowQuery,
  useRltOfferUpdateStateMutation,
} from "@/services/backend/rlt/offers/api/offer-api";
import { NavLink, useParams } from "react-router";
import { Offer } from "@/services/backend/rlt/offers/types/offer";
import { PersistenceStateUsingContext } from "@/shared/lib/persistence-state/persistence-state-info";
import t from "@/lang/lang";
import { PersistenceContextProvider } from "@/shared/lib/persistence-state/provider";
import { ErrValidationFailedContextProvider } from "@/shared/lib/errors/err-validation-failed-context";
import { DeleteOfferDialog } from "@/routes/processes/[processId]/rlt/offers/[offerId]/_components/delete-offer-dialog";
import { DuplicateOfferButton } from "@/routes/processes/[processId]/rlt/offers/[offerId]/_components/duplicate-offer-button";
import { OfferPdf } from "@/routes/processes/[processId]/rlt/offers/[offerId]/_components/offer-pdf";
import {
  DocumentPage,
  DocumentPageActions,
  DocumentPageContent,
  DocumentPageHeader,
  DocumentPageNavigation,
  DocumentPageNavigationLink,
  DocumentPageNavigationOutlet,
  DocumentPageSkeleton,
} from "@/shared/bsm/core/document/components/ui/document-page";
import { DocumentStateAlert } from "@/shared/bsm/core/document/components/ui/document-state-alert";
import { DocumentStateDropdown } from "@/shared/bsm/core/document/components/ui/document-state-dropdown";
import {
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/shared/components/ui/breadcrumb";
import { ProcessPageMainTemplate } from "@/routes/processes/[processId]/_components/process-page-main-template";
import { RltBreadcrumbs } from "@/routes/processes/[processId]/rlt/_components/rlt-breadcrumbs";
import { ReferenceNumber } from "@/services/backend/documents/components/reference-number";
import { isImmutable } from "@/services/backend/documents/lib/document-state";

export function OfferRoute() {
  const { offerId } = useParams();

  const {
    data: offer,
    isLoading,
    error,
  } = useRltOfferShowQuery({
    id: offerId!,
  });

  const breadcrumbs = offer && (
    <RltBreadcrumbs customerId={offer.customerId} processId={offer.processId!}>
      <BreadcrumbSeparator />
      <BreadcrumbItem className="hidden md:block">
        <BreadcrumbLink asChild>
          <NavLink to={`/processes/rlt/${offer.processId!}/offers`}>
            {t("Angebote")}
          </NavLink>
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbSeparator />
      <BreadcrumbItem>
        <BreadcrumbPage>
          <ReferenceNumber referenceNumber={offer.referenceNumber} />
        </BreadcrumbPage>
      </BreadcrumbItem>
    </RltBreadcrumbs>
  );

  return (
    <ProcessPageMainTemplate breadcrumbs={breadcrumbs}>
      {error && <RTKQueryErrorAlert error={error} />}
      {isLoading && <DocumentPageSkeleton />}
      {offer && <OfferPage offer={offer} />}
    </ProcessPageMainTemplate>
  );
}

function OfferPage(props: { offer: Offer }) {
  const { offer } = props;

  return (
    <PersistenceContextProvider>
      <ErrValidationFailedContextProvider>
        <Page offer={offer} />
      </ErrValidationFailedContextProvider>
    </PersistenceContextProvider>
  );
}

// The field names of each tab: the keys of offer.ReadyForCustomer and of
// the validation errors of the fields shown on the tab.
const links = [
  {
    label: t("Angebot"),
    to: "data",
    fieldName: [
      "referenceNumber",
      "date",
      "treatmentPrice",
      "incidentalCost",
      "extras",
      "areas",
    ],
  },
  { label: t("Leistungen"), to: "treatment" },
  { label: t("Kalkulation"), to: "calculation" },
  {
    label: t("Adressen & Kontakte"),
    to: "addresses-and-contacts",
    fieldName: ["serviceAddress", "postalAddress", "representative"],
  },
  { label: t("Vertrag"), to: "contract" },
];

function Page({ offer }: { offer: Offer }) {
  useResetFinalPriceOnChange(offer.pricingId);

  return (
    <DocumentPage>
      <DocumentStateAlert document={offer} />
      <DocumentPageHeader>
        <DocumentPageNavigation>
          {links.map((link) => (
            <DocumentPageNavigationLink
              key={link.to}
              to={link.to}
              fieldName={link.fieldName}
            >
              {link.label}
            </DocumentPageNavigationLink>
          ))}
        </DocumentPageNavigation>
        <DocumentPageActions>
          {isImmutable(offer) ? (
            <DuplicateOfferButton
              processId={offer.processId}
              offerId={offer.id}
            />
          ) : (
            <>
              <PersistenceStateUsingContext className="mr-2" />
              <DeleteOfferDialog offerId={offer.id} />
            </>
          )}
          <DocumentStateDropdown
            document={offer}
            mutation={useRltOfferUpdateStateMutation}
          />
          <OfferPdf offer={offer} />
        </DocumentPageActions>
      </DocumentPageHeader>
      <DocumentPageContent>
        <DocumentPageNavigationOutlet />
      </DocumentPageContent>
    </DocumentPage>
  );
}
